'use client';

import { CircularProgress, Box, Tabs, Tab } from '@packages/shared';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { SyntheticEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Captcha } from '@packages/modules/src/Captcha/Captcha';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { type GTMEventGlycerinDisplayError } from '@packages/tracking/src/types/events';
import { GlycerinErrorSeverity } from '@packages/tracking/src/types/enums';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';
import { DynamicPasswordLoginForm } from './PasswordLoginForm';
import { DynamicCustomerNrLoginForm } from './CustomerNrLoginForm';
import { useLogin } from './api/useLogin';

const messages = defineMessages({
  emailPasswordTabTitle: {
    id: 'LoginModal.tabs.email-password.title',
    defaultMessage: 'E-Mail-Adresse & Passwort',
  },
  customerNoTabTitle: {
    id: 'LoginModal.tabs.customer_no-dateofbirth.title',
    defaultMessage: 'Kundennummer & Geburtsdatum',
  },
  captchaHeadline: {
    id: 'LoginContainer.CaptchaHeadline',
    defaultMessage: 'Sicherheitsüberprüfung',
  },
});

interface LoginFormProps {
  /**
   * Callback called after successful login
   */
  onLogin: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  tab: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, tab, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={tab !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      sx={{ px: [2, 3, 5.5], py: 2 }}
      {...other}
    >
      {tab === index && children}
    </Box>
  );
};

const LoginForm = ({ onLogin }: LoginFormProps): JSX.Element => {
  const [tab, setTab] = useState(0);
  const { formatMessage } = useIntl();
  const { enableCustomerNrLogin } = useConfig();
  const dispatchGTMEvent = useTracking();
  const { login, legacyLogin, error, showCaptchaState, loading } = useLogin(onLogin);

  useEffect(() => {
    if (error) {
      dispatchGTMEvent<GTMEventGlycerinDisplayError>({
        event: 'DisplayError',
        DisplayErrorData: {
          category: 'login-form',
          errors: [
            {
              errorType: 'serverError',
              message: error,
              severity: GlycerinErrorSeverity.WARNING,
              source: 'useLogin',
            },
          ],
        },
      });
    }
  }, [error, dispatchGTMEvent]);

  const handleChangeTab = (_event: SyntheticEvent, newTab: number) => setTab(newTab);

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: '0 0 0 0',
            zIndex: 'tooltip',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size="l" />
        </Box>
      )}
      {Boolean(showCaptchaState && showCaptchaState.captchaData) && (
        <Dialog onClose={() => {}} isOpen={Boolean(showCaptchaState!.captchaData)}>
          <DialogHeader onClose={() => {}}>{formatMessage(messages.captchaHeadline)}</DialogHeader>
          <DialogContent>
            <Captcha
              captchaData={showCaptchaState!.captchaData!}
              onSuccess={showCaptchaState!.lastLogin}
            />
          </DialogContent>
        </Dialog>
      )}
      {enableCustomerNrLogin ? (
        <>
          <Tabs
            onChange={handleChangeTab}
            value={tab}
            sx={{ width: '100%', button: { flex: '1 1 auto' } }}
          >
            <Tab wrapped label={formatMessage(messages.emailPasswordTabTitle)} />
            <Tab wrapped label={formatMessage(messages.customerNoTabTitle)} />
          </Tabs>
          <TabPanel index={0} tab={tab}>
            <DynamicPasswordLoginForm
              error={error}
              onFormSubmit={(values) =>
                login({
                  email: values.email,
                  password: values.password,
                  remember: values.stayLoggedIn,
                })
              }
            />
          </TabPanel>
          <TabPanel index={1} tab={tab}>
            <DynamicCustomerNrLoginForm
              error={error}
              onFormSubmit={(values) => {
                // TODO: check why date sets back
                const dateOfBirth = values.dateOfBirth ?? new Date();
                dateOfBirth.setHours(12);
                legacyLogin(
                  {
                    accountNumber: values.customerNr,
                    birthdate: dateOfBirth,
                  },
                  values.stayLoggedIn,
                );
              }}
            />
          </TabPanel>
        </>
      ) : (
        <Box sx={{ px: [2, 3, 5.5], py: 2 }}>
          <DynamicPasswordLoginForm
            error={error}
            onFormSubmit={(values) =>
              login({
                email: values.email,
                password: values.password,
                remember: values.stayLoggedIn,
              })
            }
          />
        </Box>
      )}
    </>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default LoginForm;
